import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";

interface DefectItem {
  defectId: string;
  productId: string;
  serialNumber: string;
  defectDescription: string;
  defectCategory: "manufacturing" | "material" | "design" | "installation" | "wear_and_tear" | "other";
  severity: "minor" | "moderate" | "major" | "critical";
  firstNoticed: string;
  photosUrls: string[];
  quantity: number;
  unitCost: number;
  totalCost: number;
  replacementNeeded: boolean;
  repairPossible: boolean;
}

interface WarrantyClaimData {
  claimNumber: string;
  jobId: string;
  customerId: string;
  productWarrantyInfo: {
    warrantyType: "parts" | "labor" | "full_coverage" | "limited";
    warrantyPeriod: number; // months
    warrantyStartDate: string;
    warrantyEndDate: string;
    warrantyProvider: string;
    warrantyTerms: string;
  };
  claimDetails: {
    claimType: "defective_parts" | "workmanship" | "performance_issue" | "design_flaw" | "other";
    claimDescription: string;
    dateReported: string;
    reportedBy: string;
    contactInfo: {
      name: string;
      phone: string;
      email: string;
      address: string;
    };
    priorityLevel: "low" | "medium" | "high" | "urgent";
  };
  defectItems: DefectItem[];
  investigation: {
    investigationDate?: string;
    investigatedBy?: string;
    rootCause: string;
    findingsNotes: string;
    photosUrls: string[];
    recommendedAction: "repair" | "replace" | "refund" | "reject" | "further_investigation";
  };
  resolution: {
    approvalStatus: "pending" | "approved" | "rejected" | "escalated";
    approvedBy?: string;
    approvalDate?: string;
    resolutionType?: "repair" | "replacement" | "credit" | "refund" | "goodwill";
    estimatedCost: number;
    actualCost?: number;
    completionDate?: string;
    customerSatisfied?: boolean;
  };
  documents: {
    originalInvoice?: string;
    warrantyDocuments?: string[];
    photos?: string[];
    inspectionReports?: string[];
    correspondenceRecords?: string[];
  };
  notes: string;
  status: "draft" | "submitted" | "under_review" | "investigating" | "approved" | "rejected" | "resolved" | "closed";
}

class WarrantyClaimWizardStore {
  rootStore: RootStore;

  // UI State
  activeStep = 0;
  loading = false;
  error: string | null = null;
  success: string | null = null;

  // Form Data
  formData: WarrantyClaimData = {
    claimNumber: "",
    jobId: "",
    customerId: "",
    productWarrantyInfo: {
      warrantyType: "full_coverage",
      warrantyPeriod: 12,
      warrantyStartDate: "",
      warrantyEndDate: "",
      warrantyProvider: "",
      warrantyTerms: "",
    },
    claimDetails: {
      claimType: "defective_parts",
      claimDescription: "",
      dateReported: new Date().toISOString().split("T")[0],
      reportedBy: "",
      contactInfo: {
        name: "",
        phone: "",
        email: "",
        address: "",
      },
      priorityLevel: "medium",
    },
    defectItems: [],
    investigation: {
      rootCause: "",
      findingsNotes: "",
      photosUrls: [],
      recommendedAction: "repair",
    },
    resolution: {
      approvalStatus: "pending",
      estimatedCost: 0,
      resolutionType: "repair",
    },
    documents: {},
    notes: "",
    status: "draft",
  };

  // Lookup Data
  jobs: any[] = [];
  customers: any[] = [];
  products: any[] = [];
  warrantyProviders: any[] = [];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
    this.formData.claimNumber = this.generateClaimNumber();
  }

  // Actions
  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    if (this.activeStep > 0) {
      this.activeStep--;
    }
  };

  updateFormData = <K extends keyof WarrantyClaimData>(
    field: K,
    value: WarrantyClaimData[K]
  ) => {
    this.formData[field] = value;
    if (field === "defectItems") {
      this.calculateTotalCost();
    }
  };

  updateClaimDetails = <K extends keyof WarrantyClaimData["claimDetails"]>(
    field: K,
    value: WarrantyClaimData["claimDetails"][K]
  ) => {
    this.formData.claimDetails[field] = value;
  };

  updateProductWarrantyInfo = <K extends keyof WarrantyClaimData["productWarrantyInfo"]>(
    field: K,
    value: WarrantyClaimData["productWarrantyInfo"][K]
  ) => {
    this.formData.productWarrantyInfo[field] = value;
    if (field === "warrantyStartDate" && this.formData.productWarrantyInfo.warrantyPeriod) {
      this.calculateWarrantyEndDate();
    }
  };

  updateInvestigation = <K extends keyof WarrantyClaimData["investigation"]>(
    field: K,
    value: WarrantyClaimData["investigation"][K]
  ) => {
    this.formData.investigation[field] = value;
  };

  updateResolution = <K extends keyof WarrantyClaimData["resolution"]>(
    field: K,
    value: WarrantyClaimData["resolution"][K]
  ) => {
    this.formData.resolution[field] = value;
  };

  addDefectItem = (item: DefectItem) => {
    this.formData.defectItems.push(item);
    this.calculateTotalCost();
  };

  removeDefectItem = (index: number) => {
    this.formData.defectItems.splice(index, 1);
    this.calculateTotalCost();
  };

  updateDefectItem = (index: number, item: DefectItem) => {
    this.formData.defectItems[index] = item;
    this.calculateTotalCost();
  };

  generateClaimNumber = (): string => {
    const date = new Date();
    const year = date.getFullYear().toString().slice(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, "0");
    return `WC-${year}${month}${day}-${random}`;
  };

  generateDefectId = (): string => {
    return `DEF-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  };

  calculateWarrantyEndDate = () => {
    if (this.formData.productWarrantyInfo.warrantyStartDate && this.formData.productWarrantyInfo.warrantyPeriod) {
      const startDate = new Date(this.formData.productWarrantyInfo.warrantyStartDate);
      const endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + this.formData.productWarrantyInfo.warrantyPeriod);
      this.formData.productWarrantyInfo.warrantyEndDate = endDate.toISOString().split("T")[0];
    }
  };

  calculateTotalCost = () => {
    const totalCost = this.formData.defectItems.reduce(
      (sum, item) => sum + item.totalCost,
      0
    );
    this.formData.resolution.estimatedCost = totalCost;
  };

  checkWarrantyValidity = (): boolean => {
    const currentDate = new Date();
    const warrantyEndDate = new Date(this.formData.productWarrantyInfo.warrantyEndDate);
    return currentDate <= warrantyEndDate;
  };

  // Validation
  validateCurrentStep = (): boolean => {
    this.error = null;

    switch (this.activeStep) {
      case 0:
        if (!this.formData.jobId) {
          this.error = "Please select a job/project";
          return false;
        }
        if (!this.formData.customerId) {
          this.error = "Please select a customer";
          return false;
        }
        if (!this.formData.claimDetails.claimDescription) {
          this.error = "Please provide a claim description";
          return false;
        }
        if (!this.formData.claimDetails.reportedBy) {
          this.error = "Please specify who reported this claim";
          return false;
        }
        return true;

      case 1:
        if (!this.formData.productWarrantyInfo.warrantyStartDate) {
          this.error = "Please specify warranty start date";
          return false;
        }
        if (!this.formData.productWarrantyInfo.warrantyProvider) {
          this.error = "Please specify warranty provider";
          return false;
        }
        if (!this.checkWarrantyValidity()) {
          this.error = "Warranty period has expired";
          return false;
        }
        return true;

      case 2:
        if (this.formData.defectItems.length === 0) {
          this.error = "Please add at least one defective item";
          return false;
        }

        const invalidItems = this.formData.defectItems.some(
          (item) => !item.productId || !item.defectDescription || item.quantity <= 0
        );
        if (invalidItems) {
          this.error = "Please complete all defect item details";
          return false;
        }
        return true;

      case 3:
        if (!this.formData.investigation.rootCause) {
          this.error = "Please provide root cause analysis";
          return false;
        }
        if (!this.formData.investigation.recommendedAction) {
          this.error = "Please specify recommended action";
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  // API Actions
  loadInitialData = async () => {
    this.loading = true;
    this.error = null;

    try {
      // Load jobs
      if (this.rootStore.jobStore) {
        await this.rootStore.jobStore.find({
          query: {
            $limit: 100,
            status: "active",
          },
        });
      }

      // Load customers/accounts
      if (this.rootStore.customersStore) {
        await this.rootStore.customersStore.find({
          query: {
            $limit: 100,
            active: true,
          },
        });
      }

      // Load products
      if (this.rootStore.productsStore) {
        await this.rootStore.productsStore.find({
          query: {
            $limit: 500,
            active: true,
          },
        });
      }

      runInAction(() => {
        this.jobs = this.rootStore.jobStore?.entities || [];
        this.customers = this.rootStore.customersStore?.entities || [];
        this.products = this.rootStore.productsStore?.entities || [];

        // Mock warranty providers (would come from API)
        this.warrantyProviders = [
          { id: "1", name: "Manufacturer Warranty", type: "manufacturer" },
          { id: "2", name: "Extended Warranty Co.", type: "third_party" },
          { id: "3", name: "Installation Warranty", type: "contractor" },
          { id: "4", name: "Parts Supplier Warranty", type: "supplier" },
        ];

        this.loading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to load initial data";
        this.loading = false;
      });
    }
  };

  submitWarrantyClaim = async () => {
    if (!this.validateCurrentStep()) {
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      // Create warranty claim record
      const claimData = {
        ...this.formData,
        submittedAt: new Date().toISOString(),
        submittedBy: this.rootStore.authStore?.user?.id,
        status: "submitted",
      };

      // API call would go here
      await new Promise((resolve) => setTimeout(resolve, 1500)); // Simulated API call

      runInAction(() => {
        this.success = "Warranty claim submitted successfully!";
        this.loading = false;

        // Reset form after successful submission
        setTimeout(() => {
          this.reset();
          this.rootStore.routerStore.goTo("Dashboard");
        }, 2000);
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to submit warranty claim";
        this.loading = false;
      });
    }
  };

  // Computed Values
  get users() {
    return this.rootStore.userStore?.entities || [];
  }

  get selectedJob() {
    return this.jobs.find((j) => j.id === this.formData.jobId);
  }

  get selectedCustomer() {
    return this.customers.find((c) => c.id === this.formData.customerId);
  }

  get selectedWarrantyProvider() {
    return this.warrantyProviders.find(
      (p) => p.id === this.formData.productWarrantyInfo.warrantyProvider
    );
  }

  get totalDefectValue() {
    return this.formData.defectItems.reduce((sum, item) => sum + item.totalCost, 0);
  }

  get criticalDefects() {
    return this.formData.defectItems.filter(item => item.severity === "critical");
  }

  get majorDefects() {
    return this.formData.defectItems.filter(item => item.severity === "major");
  }

  get warrantyExpired() {
    return !this.checkWarrantyValidity();
  }

  get daysUntilWarrantyExpiry() {
    const currentDate = new Date();
    const warrantyEndDate = new Date(this.formData.productWarrantyInfo.warrantyEndDate);
    const diffTime = warrantyEndDate.getTime() - currentDate.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  get getProductById() {
    return (productId: string) => this.products.find((p) => p.id === productId);
  }

  get getWarrantyProviderById() {
    return (providerId: string) => this.warrantyProviders.find((p) => p.id === providerId);
  }

  // Reset
  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.formData = {
      claimNumber: this.generateClaimNumber(),
      jobId: "",
      customerId: "",
      productWarrantyInfo: {
        warrantyType: "full_coverage",
        warrantyPeriod: 12,
        warrantyStartDate: "",
        warrantyEndDate: "",
        warrantyProvider: "",
        warrantyTerms: "",
      },
      claimDetails: {
        claimType: "defective_parts",
        claimDescription: "",
        dateReported: new Date().toISOString().split("T")[0],
        reportedBy: "",
        contactInfo: {
          name: "",
          phone: "",
          email: "",
          address: "",
        },
        priorityLevel: "medium",
      },
      defectItems: [],
      investigation: {
        rootCause: "",
        findingsNotes: "",
        photosUrls: [],
        recommendedAction: "repair",
      },
      resolution: {
        approvalStatus: "pending",
        estimatedCost: 0,
        resolutionType: "repair",
      },
      documents: {},
      notes: "",
      status: "draft",
    };
  };
}

export default WarrantyClaimWizardStore;